import React, { useCallback, useMemo, useState } from 'react';
import { List, Set as ImmutableSet } from 'immutable';
import { Combobox, ComboboxInput } from '@headlessui/react';

import { setSnackbar } from 'flow/actions/app-actions';
import { segmentTrack } from 'flow/actions/segment-actions';
import { addTicketMembers } from 'flow/actions/ticket-actions';
import SearchInput from 'flow/components/ui-components/SearchInput';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';
import { selectMembers } from 'flow/selectors/TicketsSelectors';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { TypedMap } from 'flow/types/TypedMap';
import { sortByName } from 'flow/utils';
import { filterExtensionsBySearchableInfo } from 'flow/utils/extension-utils';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import AccessCandidateList from './AccessCandidateList';
import AccessNoCandidates from './AccessNoCandidates';
import useTicketAction from 'flow/hooks/useTicketAction';
import { ticketWriteWasRefused } from 'flow/utils/ticket-utils';

interface Props {
    ticketKey: TicketKey;
}

/**
 * Search field that adds a colleague to the ticket the moment one is picked —
 * there is no separate confirm step, so the list below updates as you go.
 *
 * A combobox rather than a field with a panel of our own. `immediate` opens the
 * list on focus, because who can be added is the question the field asks and on a
 * small customer there is nothing to type. It also brings the three things a
 * hand-rolled panel had to do without: the list is anchored and portalled, so the
 * dialog's own overflow cannot clip it; Escape closes the list rather than
 * reaching the dialog behind it; and the arrow keys walk the rows.
 *
 * It holds no value: a pick is an act, not a selection to display. The field
 * therefore clears itself after each one, and several people can be added in a row.
 */
const TicketAccessAddPeople = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();

    const [filterText, setFilterText] = useState('');

    const extensions = useAppSelector(selectExtensions);
    const memberKeys = useAppSelector(
        (state) => selectMembers(state, ticketKey) || ImmutableSet(),
    );

    // Sorted apart from the filtering below, which is the point of the split: the
    // order can only change when the extensions map does, so typing costs a filter
    // and nothing more. Kept in the component rather than a shared selector, which
    // would hold a sorted copy for the life of the app to save one sort per opening.
    const extensionsSortedByName = useMemo(
        () => extensions.sort(sortByName),
        [extensions],
    );

    const candidateKeys: ExtensionKey[] = useMemo(() => {
        const notAlreadyMembers = extensionsSortedByName.filter(
            (extension: TypedMap<Extension>) =>
                !memberKeys.contains(extension.get('key')),
        );

        // Keys rather than extensions: a row reads its own name and avatar from
        // the store, and the virtualiser wants a plain array. An empty filter
        // matches every extension, so this is the whole list until the viewer
        // narrows it.
        return filterExtensionsBySearchableInfo(notAlreadyMembers, filterText)
            .map((extension: TypedMap<Extension>) => extension.get('key'))
            .toArray();
    }, [extensionsSortedByName, memberKeys, filterText]);

    const hasCandidates = candidateKeys.length > 0;

    const onSelectCandidate = useCallback(
        (extensionKey: ExtensionKey | null) => {
            // Clearing the field reports a null selection, which is no pick.
            if (!extensionKey) return;

            runTicketAction(
                addTicketMembers(ticketKey, List([extensionKey])),
            ).catch((error?: { status?: number }) => {
                // The refusal already opened the Access denied dialog, so the
                // snackbar is for everything else.
                if (ticketWriteWasRefused(error)) return;
                dispatch(setSnackbar({ i18nextMessage: 'snackbar.error' }));
            });
            setFilterText('');
            dispatch(
                segmentTrack(TICKET_STRINGS.ADD_MEMBERS, {
                    component: 'ticket access modal',
                }),
            );
        },
        [dispatch, ticketKey],
    );

    return (
        <Combobox
            immediate={true}
            value={null}
            onChange={onSelectCandidate}
            onClose={() => setFilterText('')}
            // Virtual mode renders no row at all for an empty set, so the
            // no-results panel needs the plain mode — the same emptiness picks
            // both, right here, rather than in two components that could drift.
            {...(hasCandidates ? { virtual: { options: candidateKeys } } : {})}
        >
            <ComboboxInput
                as={SearchInput}
                iconKey="search"
                placeholder={i18next.t('tickets.access.add_people')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilterText(event.target.value)
                }
            />
            {hasCandidates ? <AccessCandidateList /> : <AccessNoCandidates />}
        </Combobox>
    );
};

export default TicketAccessAddPeople;
